import { useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, CircleDashed, AlertCircle, Undo2, Check, Pencil } from "lucide-react";
import { formatMoney } from "@/components/KPICard";
import { formatDMY } from "@/lib/dates";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TENANTS } from "@/constants/testIds";

const STATUS = {
  paid: { label: "Pagado", tone: "text-emerald-500", bg: "bg-emerald-500/10 border-emerald-500/20", Icon: CheckCircle2 },
  pending: { label: "Pendiente", tone: "text-sky-500", bg: "bg-sky-500/10 border-sky-500/20", Icon: CircleDashed },
  overdue: { label: "Atrasado", tone: "text-rose-500", bg: "bg-rose-500/10 border-rose-500/20", Icon: AlertCircle },
  partial: { label: "Parcial", tone: "text-amber-500", bg: "bg-amber-500/10 border-amber-500/20", Icon: CircleDashed },
};

export default function PaymentCalendar({ payments, onChanged, canEdit }) {
  const [dlg, setDlg] = useState(null); // payment
  const [amount, setAmount] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [editDlg, setEditDlg] = useState(null);   // payment being edited
  const [editAmount, setEditAmount] = useState("");

  const sorted = useMemo(() => [...payments].sort((a, b) => (a.due_date > b.due_date ? -1 : 1)), [payments]);

  const openRegister = (p) => {
    setDlg(p);
    setAmount(String(p.expected_amount));
    setPaidDate(new Date().toISOString().slice(0, 10));
  };

  const openEdit = (p) => {
    setEditDlg(p);
    // Prefill with the meaningful amount: paid if collected, expected if pending.
    const seed = (p.status === "paid" || p.status === "partial") && p.paid_amount
      ? p.paid_amount
      : p.expected_amount;
    setEditAmount(String(seed));
  };

  const register = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/payments/${dlg.id}/register`, {
        paid_amount: parseFloat(amount) || 0,
        paid_date: paidDate,
      });
      toast.success("Pago registrado y transacción generada");
      setDlg(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const unregister = async (p) => {
    if (!window.confirm(`¿Revertir pago del período ${p.period}?`)) return;
    try {
      await api.post(`/payments/${p.id}/unregister`);
      toast.success("Pago revertido");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      const val = parseFloat(editAmount);
      if (Number.isNaN(val) || val < 0) {
        toast.error("Ingresá un monto válido");
        return;
      }
      await api.patch(`/payments/${editDlg.id}`, { amount: val });
      const isPaid = editDlg.status === "paid" || editDlg.status === "partial";
      toast.success(isPaid ? "Monto pagado actualizado" : "Monto estimado actualizado");
      setEditDlg(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!payments.length) {
    return (
      <div className="rounded-xl border border-border/60 bg-card p-8 text-center text-muted-foreground text-sm">
        Sin pagos generados. Cree un contrato para generar automáticamente los pagos.
      </div>
    );
  }

  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-3">Calendario de pagos</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {sorted.map((p) => {
          const st = STATUS[p.status] || STATUS.pending;
          const Icon = st.Icon;
          return (
            <div key={p.id} className={`rounded-lg border p-3 ${st.bg}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold num">{p.period}</div>
                <Icon className={`w-4 h-4 ${st.tone}`} />
              </div>
              <div className="mt-2 text-sm font-heading font-semibold num">
                {formatMoney(p.expected_amount, p.currency)}
              </div>
              <div className={`text-[10px] uppercase tracking-widest ${st.tone}`}>{st.label}</div>
              <div className="text-[11px] text-muted-foreground mt-1 num">Vence {formatDMY(p.due_date)}</div>
              {canEdit && p.status !== "paid" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-2 h-7 px-2 text-xs w-full"
                  onClick={() => openRegister(p)}
                  data-testid={`${TENANTS.paymentRegisterBtn}-${p.id}`}
                >
                  <Check className="w-3 h-3 mr-1" /> Registrar pago
                </Button>
              )}
              {canEdit && p.status === "paid" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-2 h-7 px-2 text-xs w-full"
                  onClick={() => unregister(p)}
                >
                  <Undo2 className="w-3 h-3 mr-1" /> Revertir
                </Button>
              )}
              {canEdit && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-1 h-7 px-2 text-xs w-full opacity-80 hover:opacity-100"
                  onClick={() => openEdit(p)}
                  data-testid={`payment-edit-amount-${p.id}`}
                  title={p.status === "paid" || p.status === "partial"
                    ? "Corregir el monto realmente cobrado"
                    : "Ajustar el monto estimado para este mes"}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Editar monto
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar pago</DialogTitle>
            <DialogDescription>Se crea automáticamente un ingreso en la propiedad con conversión a USD.</DialogDescription>
          </DialogHeader>
          {dlg && (
            <form onSubmit={register} className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Período: <strong className="text-foreground">{dlg.period}</strong> · Vencimiento: <strong className="text-foreground num">{formatDMY(dlg.due_date)}</strong>
              </div>
              <div>
                <Label>Monto pagado ({dlg.currency})</Label>
                <Input type="number" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                <Label className="text-[11px] uppercase tracking-widest text-emerald-500 font-semibold">📅 Fecha en que recibiste el pago</Label>
                <Input
                  type="date"
                  required
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  className="mt-2 h-11 text-base"
                  data-testid="payment-paid-date"
                />
                <div className="text-[11px] text-muted-foreground mt-1 num">
                  Vas a registrar: <strong className="text-foreground">{formatDMY(paidDate)}</strong>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDlg(null)}>Cancelar</Button>
                <Button type="submit">Confirmar</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editDlg} onOpenChange={(v) => !v && setEditDlg(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar monto del recibo</DialogTitle>
            <DialogDescription>
              {editDlg && (editDlg.status === "paid" || editDlg.status === "partial")
                ? "Corregí el monto REALMENTE cobrado. La transacción de ingreso se actualiza y el dashboard refleja el cambio."
                : "Ajustá el monto ESTIMADO para este mes (por ejemplo, un cambio puntual en la renta). No afecta a los demás meses."}
            </DialogDescription>
          </DialogHeader>
          {editDlg && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Período: <strong className="text-foreground">{editDlg.period}</strong> ·{" "}
                Vencimiento: <strong className="text-foreground num">{formatDMY(editDlg.due_date)}</strong>
              </div>
              <div className="rounded-lg border border-border/60 bg-secondary/30 p-3 text-xs space-y-1">
                <div>Estimado original: <strong className="num">{formatMoney(editDlg.expected_amount, editDlg.currency)}</strong></div>
                {(editDlg.status === "paid" || editDlg.status === "partial") && (
                  <div>Cobrado actual: <strong className="num">{formatMoney(editDlg.paid_amount, editDlg.currency)}</strong></div>
                )}
              </div>
              <div>
                <Label>
                  Nuevo monto ({editDlg.currency}) —{" "}
                  {editDlg.status === "paid" || editDlg.status === "partial" ? "cobrado real" : "estimado"}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  data-testid="payment-edit-amount-input"
                  className="h-11 text-base"
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDlg(null)}>Cancelar</Button>
                <Button type="submit" data-testid="payment-edit-amount-submit">Guardar</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
